import { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import PageHeader from "./components/PageHeader";
import TodoForm from "./components/TodoForm";
import TodoList from "./components/TodoList";
import CategoryModal from "./components/CategoryModal";
import RecurrenceModal from "./components/RecurrenceModal";
import CalendarView from "./components/CalendarView";
import ExternalCalendarsPanel from "./components/ExternalCalendarsPanel";
import LoginPage from "./components/LoginPage";
import AdminPanel from "./components/AdminPanel";
import ChangePasswordModal from "./components/ChangePasswordModal";
import { MenuIcon } from "./components/Icons";

const TODOS_API = "/api/todos";
const CATS_API  = "/api/categories";
const RECS_API  = "/api/recurrences";
const EXT_CALS_API = "/api/external-calendars";

export default function App() {
  const [user, setUser]             = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if already logged in on mount
  useEffect(() => {
    fetch("/api/auth/me", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.user) setUser(data.user); })
      .catch(() => {})
      .finally(() => setAuthChecked(true));
  }, []);

  const handleLogout = async () => {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setUser(null);
  };

  if (!authChecked) return <div className="app-layout"><div className="loading"><div className="spinner"/>Loading…</div></div>;
  if (!user)        return <LoginPage onLogin={setUser} />;

  return <AuthenticatedApp user={user} onLogout={handleLogout} />;
}

function AuthenticatedApp({ user, onLogout }) {
  const [todos, setTodos]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("all");
  const [search, setSearch]         = useState("");
  const [error, setError]           = useState(null);

  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null); // null = All Tasks
  const [showAddCat, setShowAddCat] = useState(false);
  const [addCatParentId, setAddCatParentId] = useState(null);
  const [editCat, setEditCat] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [recurrences, setRecurrences] = useState([]);
  const [showAddRecurrence, setShowAddRecurrence] = useState(false);
  const [view, setView] = useState("list");

  const [externalCalendars, setExternalCalendars] = useState([]);
  const [extCalOpen, setExtCalOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(CATS_API, { credentials: "include" });
      if (res.ok) setCategories(await res.json());
    } catch {}
  }, []);

  const fetchRecurrences = useCallback(async () => {
    try {
      const res = await fetch(RECS_API, { credentials: "include" });
      if (res.ok) setRecurrences(await res.json());
    } catch {}
  }, []);

  const fetchExternalCalendars = useCallback(async () => {
    try {
      const res = await fetch(EXT_CALS_API, { credentials: "include" });
      if (res.ok) setExternalCalendars(await res.json());
    } catch {}
  }, []);

  const fetchTodos = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter === "active") params.set("completed", "false");
      if (filter === "done")   params.set("completed", "true");
      if (["high","medium","low"].includes(filter)) params.set("priority", filter);
      if (search.trim()) params.set("search", search.trim());
      if (selectedCat)   params.set("category_id", selectedCat);

      const res = await fetch(TODOS_API + "?" + params, { credentials: "include" });
      if (!res.ok) throw new Error();
      setTodos(await res.json());
      setError(null);
    } catch {
      setError("Could not connect to server. Is the backend running?");
    } finally {
      setLoading(false);
    }
  }, [filter, search, selectedCat]);

  useEffect(() => { fetchCategories(); },       [fetchCategories]);
  useEffect(() => { fetchTodos(); },            [fetchTodos]);
  useEffect(() => { fetchRecurrences(); },      [fetchRecurrences]);
  useEffect(() => { fetchExternalCalendars(); },[fetchExternalCalendars]);

  const addTodo = async ({ title, description, priority, category_id, due_date, due_time }) => {
    try {
      const body = { title, description, priority };
      if (category_id) body.category_id = category_id;
      if (due_date) { body.due_date = due_date; if (due_time) body.due_time = due_time; }
      const res = await fetch(TODOS_API, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to add task");
      }
      fetchTodos(); fetchCategories();
    } catch (err) { setError(err.message || "Failed to add task"); }
  };

  const updateTodo = async (id, fields) => {
    try {
      await fetch(TODOS_API + "/" + id, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      fetchTodos(); fetchCategories();
    } catch { setError("Failed to update task"); }
  };

  const toggleTodo = async (todo) => {
    if (todo.recurrence_id && !todo.completed) return deleteTodo(todo.id);
    try {
      await fetch(TODOS_API + "/" + todo.id, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ completed: !todo.completed }),
      });
      fetchTodos();
    } catch { setError("Failed to update task"); }
  };

  const deleteTodo = async (id) => {
    try {
      await fetch(TODOS_API + "/" + id, { method: "DELETE", credentials: "include" });
      fetchTodos(); fetchCategories();
    } catch { setError("Failed to delete task"); }
  };

  const clearCompleted = async () => {
    try {
      await fetch(TODOS_API + "?confirm=true", { method: "DELETE", credentials: "include" });
      fetchTodos(); fetchCategories();
    } catch { setError("Failed to clear completed"); }
  };

  const addRecurrence = async (data) => {
    try {
      const res = await fetch(RECS_API, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error();
      setShowAddRecurrence(false);
      fetchRecurrences();
    } catch { setError("Failed to add recurring task"); }
  };

  const deleteRecurrence = async (id) => {
    try {
      await fetch(RECS_API + "/" + id, { method: "DELETE", credentials: "include" });
      fetchRecurrences();
    } catch { setError("Failed to delete recurring task"); }
  };

  const addCategory = async ({ name, color, parent_id }) => {
    try {
      const res = await fetch(CATS_API, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color, parent_id }),
      });
      if (!res.ok) throw new Error();
      setShowAddCat(false);
      fetchCategories();
    } catch { setError("Failed to add category"); }
  };

  const updateCategory = async ({ id, name, color }) => {
    try {
      const res = await fetch(CATS_API + "/" + id, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color }),
      });
      if (!res.ok) throw new Error();
      setEditCat(null);
      fetchCategories();
    } catch { setError("Failed to update category"); }
  };

  const addExternalCalendar = async ({ name, url }) => {
    try {
      const res = await fetch(EXT_CALS_API, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, url }),
      });
      if (!res.ok) throw new Error();
      fetchExternalCalendars();
    } catch { setError("Failed to add calendar"); }
  };

  const deleteExternalCalendar = async (id) => {
    try {
      await fetch(EXT_CALS_API + "/" + id, { method: "DELETE", credentials: "include" });
      fetchExternalCalendars();
    } catch { setError("Failed to delete calendar"); }
  };

  const saveExternalCalendar = async (id, fields) => {
    try {
      await fetch(EXT_CALS_API + "/" + id, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields),
      });
      fetchExternalCalendars();
    } catch { setError("Failed to update calendar"); }
  };

  const deleteCategory = async (id) => {
    try {
      await fetch(CATS_API + "/" + id, { method: "DELETE", credentials: "include" });
      if (selectedCat === id) setSelectedCat(null);
      fetchCategories(); fetchTodos();
    } catch { setError("Failed to delete category"); }
  };

  const activeCat = categories.find(c => c.id === selectedCat);
  const total     = todos.length;
  const done      = todos.filter(t => t.completed).length;
  const active    = total - done;

  if (loading) return (
    <div className="app-layout">
      <div className="loading"><div className="spinner"/>Loading tasks...</div>
    </div>
  );

  return (
    <div className="app-layout">
      {sidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)}/>
      )}

      <Sidebar
        categories={categories}
        selectedCat={selectedCat}
        onSelect={(id) => { setSelectedCat(id); setSidebarOpen(false); }}
        onDelete={deleteCategory}
        onAddClick={() => { setAddCatParentId(null); setShowAddCat(true); }}
        onAddSubClick={(parentId) => { setAddCatParentId(parentId); setShowAddCat(true); }}
        onEditClick={(cat) => setEditCat(cat)}
        recurrences={recurrences}
        onDeleteRecurrence={deleteRecurrence}
        onAddRecurrence={() => setShowAddRecurrence(true)}
        onOpenExternalCalendars={() => setExtCalOpen(true)}
        sidebarOpen={sidebarOpen}
      />

      <main className="main">
        <div className="mobile-header">
          <button className="hamburger" onClick={() => setSidebarOpen(true)}>
            <MenuIcon/>
          </button>
          <span style={{ fontFamily: "var(--font-display)", fontSize: "1.1rem", flex: 1 }}>
            {activeCat ? activeCat.name : "All Tasks"}
          </span>
          <button className="mobile-header-btn" onClick={() => setChangePwOpen(true)} title="Account">⚙</button>
          <button className="mobile-header-btn mobile-signout" onClick={onLogout} title="Sign out">Sign out</button>
        </div>

        <div className="cat-pills">
          <button className={`cat-pill${!selectedCat ? " active" : ""}`} onClick={() => setSelectedCat(null)}>All</button>
          {categories.filter(c => !c.parent_id).map(cat => (
            <button
              key={cat.id}
              className={`cat-pill${selectedCat === cat.id ? " active" : ""}`}
              onClick={() => setSelectedCat(cat.id)}
            >
              <span className="cat-pill-dot" style={{ background: cat.color }} />
              {cat.name}
              {cat.active_count > 0 && <span className="cat-pill-count">{cat.active_count}</span>}
            </button>
          ))}
        </div>

        <div className={view === "calendar" ? "main-inner main-inner-wide" : "main-inner"}>
          <PageHeader activeCat={activeCat} active={active} total={total} done={done} user={user} onLogout={onLogout} onAdminClick={() => setAdminOpen(true)} onChangePassword={() => setChangePwOpen(true)}/>

          <div className="view-tabs">
            <button className={"view-tab" + (view === "list"     ? " active" : "")} onClick={() => setView("list")}>List</button>
            <button className={"view-tab" + (view === "calendar" ? " active" : "")} onClick={() => setView("calendar")}>Calendar</button>
          </div>

          {view === "list" && <>
            <TodoForm
              categories={categories}
              defaultCategoryId={selectedCat ? String(selectedCat) : ""}
              onSubmit={addTodo}
            />

            {error && <div className="error-banner">{error}</div>}

            <TodoList
              todos={todos}
              done={done}
              search={search}
              filter={filter}
              onSearch={setSearch}
              onFilter={setFilter}
              onToggle={toggleTodo}
              onDelete={deleteTodo}
              onEdit={updateTodo}
              onClearCompleted={clearCompleted}
              categories={categories}
            />
          </>}

          {view === "calendar" && <>
            {error && <div className="error-banner">{error}</div>}
            <CalendarView todos={todos} recurrences={recurrences} />
          </>}
        </div>
      </main>

      {showAddCat && (
        <CategoryModal
          onClose={() => { setShowAddCat(false); setAddCatParentId(null); }}
          onSubmit={addCategory}
          categories={categories}
          defaultParentId={addCatParentId}
        />
      )}

      {editCat && (
        <CategoryModal
          onClose={() => setEditCat(null)}
          onSubmit={updateCategory}
          categories={categories}
          editCategory={editCat}
        />
      )}

      <ExternalCalendarsPanel
        open={extCalOpen}
        onClose={() => setExtCalOpen(false)}
        calendars={externalCalendars}
        onAdd={addExternalCalendar}
        onDelete={deleteExternalCalendar}
        onSave={saveExternalCalendar}
      />

      {user?.is_admin && (
        <AdminPanel open={adminOpen} onClose={() => setAdminOpen(false)} />
      )}

      {changePwOpen && (
        <ChangePasswordModal onClose={() => setChangePwOpen(false)} />
      )}

      {showAddRecurrence && (
        <RecurrenceModal
          onClose={() => setShowAddRecurrence(false)}
          onSubmit={addRecurrence}
          categories={categories}
        />
      )}
    </div>
  );
}
